/**
 * @file HoldingProduct.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-17
 * @modified 2026-09-17
 * @description A contract agreed before its product exists in inventory. The
 *              product dropdown offers "Holding product" first; choosing it asks
 *              for a description instead ("Winter 26 dairy blend"), and the
 *              contract saves with no item. Editing the contract later and
 *              choosing the real item replaces it.
 *
 *              Used by ContractPricingModal (single products and product-group
 *              lines) and StraightsContractModal.
 */

import React from 'react';
import Input from './Input';
import './HoldingProduct.css';

/** The dropdown value standing for "no item yet". Never sent to the server. */
export const HOLDING_PRODUCT = '__holding__';

export const isHoldingProduct = (value) => value === HOLDING_PRODUCT;

/** The option that sits first in a contract's product dropdown. */
export const holdingProductOption = () => ({
  value: HOLDING_PRODUCT,
  label: 'Holding product',
  searchText: 'holding product placeholder not set up yet add item later tbc',
  renderLabel: (
    <span className="option-name">
      <span className="option-name-text">Holding product</span>
      <span className="option-status-badge is-holding">ADD ITEM LATER</span>
    </span>
  )
});

/** The dropdown value for a saved contract: its item, or the holding product. */
export const productValueFor = (contract) => {
  if (contract?.inventory_item_id != null) return contract.inventory_item_id.toString();
  return contract?.id && contract?.holding_product_name ? HOLDING_PRODUCT : '';
};

/** What to send for a product choice: an item id, or no item and a description. */
export const productPayloadFor = (value, holdingName) => (isHoldingProduct(value)
  ? { inventory_item_id: null, holding_product_name: (holdingName || '').trim() || null }
  : { inventory_item_id: value || null, holding_product_name: null });

/** The description box shown under the product dropdown while it's a holding product. */
export function HoldingProductDescription({ id, value, onChange, disabled }) {
  return (
    <div className="holding-product-description">
      <label htmlFor={id}>Holding product description *</label>
      <Input
        id={id}
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="What it's for, e.g. Winter 26 dairy blend"
        maxLength={120}
      />
      <span className="holding-product-hint">Choose the real product here once it's set up in inventory.</span>
    </div>
  );
}
